import React, { Fragment, FunctionComponent } from 'react';
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  Box,
  Typography,
} from '@mui/material';
import classnames from 'classnames';
import { Constants } from '../../../../utils';
import { IBreakdownReport } from '../../../../interfaces/calculator.interface';
import {
  ICategoryReport,
  ISubCategoryReport,
  ILineReport,
} from '../../../../interfaces/report.interface';
import { IInterventionsChecklist } from '../../../../interfaces/interventions.interface';
import { getReportBreakdown } from './ReportBreakdownMap';

import { useReportBreakdownStyles, useTableCellStyles } from './ReportBreakdown.styles';


const CELL_PARTITIONS: Record<string, string> = {
  1: 'full',
  2: 'half',
  3: 'third',
};

interface IProps {
  report: IBreakdownReport;
  interventionsChecklist: IInterventionsChecklist;
}

const ReportBreakdown: FunctionComponent<IProps> = ({
  report,
  interventionsChecklist,
}) => {

  const classes = useReportBreakdownStyles();
  const tableCellClasses = useTableCellStyles();
  let rowIdx = 1;
  const getRowIndex = () => `row__${rowIdx++}`;

  const buildSubCells = (
    values: string[] | number[] = [],
    isDefault = true
  ) => {
    let partitionClass = CELL_PARTITIONS[3];
    let validValues = [...values];

    if (!isDefault) {
      validValues = (values as any[]).filter((t) => t !== '');
      const numberOfValues = validValues.length;
      partitionClass = CELL_PARTITIONS[numberOfValues];
    }

    return validValues.map((val) => (
      <Box
        key={getRowIndex()}
        className={classnames(
          classes.valueCell,
          classes[partitionClass as keyof typeof classes]
        )}        
      >
        {val === 'undefined' ? Constants.STRING_TYPES.EMPTY : val}
      </Box>
    ));
  };

  const getLines = (lines: ILineReport[] = []) =>
    lines.map((line) => (
      <Fragment key={getRowIndex()}>
        <TableRow>
          <TableCell
            className={classnames(classes.descriptionCol, classes.smallText)}
          >
            <Typography className={classes.descriptionText}>
              {line.description}
            </Typography>
            {Boolean(line.note) && (
              <Typography className={classes.noteText}>
                {`Note: ${line.note}`}
              </Typography>
            )}
          </TableCell>
          <TableCell className={classes.composedCell}>
            <Box className={classes.multiCont}>
              {buildSubCells(line.values, line.displayDefault)}
            </Box>
          </TableCell>
        </TableRow>
      </Fragment>
    ));

  const getSubCategories = (subCategories: ISubCategoryReport[] = []) =>
    subCategories.map((s) =>
      s.visible ? (
        <Fragment key={getRowIndex()}>
          <TableRow>
            <TableCell
              className={classnames(
                classes.descriptionCol,
                classes.subsectionTitle
              )}
              colSpan={2}
            >
              {s.title}
            </TableCell>
          </TableRow>
          {getLines(s.lines)}
        </Fragment>
      ) : null
    );

  return (
    <div className={classes.tableContainer} id='healthyStepsOverview'>
       <Typography
              variant="h2"
              className={classes.contentTitle}
            >
              HealthySteps Overview Data
            </Typography>
      <Table className={classes.table}>
        <TableBody>
          {getReportBreakdown(interventionsChecklist, report).map(
            (cat: ICategoryReport) => {
              const catCols = cat.resultsHeaders;
              return (
                <Fragment key={getRowIndex()}>
                  <TableRow>
                    <TableCell
                      className={classnames(
                        classes.descriptionCol,
                        classes.sectionTitle
                      )}
                      {...(!catCols && { colSpan: 2 })}
                    >
                      {cat.title}
                    </TableCell>
                    {catCols && (
                      <TableCell
                        className={classnames(
                          classes.composedCell,
                          classes.sectionTitle
                        )}
                      >
                        <Box className={classes.multiCont}>
                          {buildSubCells(catCols)}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                  {cat.subCategories && getSubCategories(cat.subCategories)}
                </Fragment>
              );
            }
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default ReportBreakdown;
